import { createStore } from "zustand/vanilla";
import type { ConfigurationItemAddon } from "../types/configuration-item-addon.type";

// View-local store for addon selections of the diet currently being configured.
// It is intentionally NOT persisted and lives outside the cart store: addon
// choices are just view state on the configuration screen (like calories/dates
// kept in the form) and are merged into the cart item only on save (add / edit).
export type AddonsConfigState = {
  addons: ConfigurationItemAddon[];
  dietId: string | null;
};

export type AddonsConfigActions = {
  // Replace the whole selection (used to seed from an edited cart item).
  seedAddons: (addons: ConfigurationItemAddon[]) => void;
  // All day entries currently selected for a single addon.
  getAddonEntries: (addonId: string) => ConfigurationItemAddon[];
  // Replace all day entries for a single addon at once (the modal commits the
  // full per-addon selection on "add"). Passing an empty list clears the addon.
  setAddonEntries: (
    addonId: string,
    entries: { date: string; quantity: number }[],
  ) => void;
  // Drop selections whose day is no longer among the diet delivery days. Keeps
  // the state ready for PUT: a sub-item date must be one of the item's `dates`,
  // otherwise the backend rejects the whole update with a 422 (MPL-3694).
  pruneToDates: (allowedDates: string[]) => void;
  // Drop selections whose addon is not in the current diet's catalog: an addon
  // of another catering (or one withdrawn from the offer) has no mapping on the
  // merchant side and makes the whole item quote fail with a 500 (MPL-3851).
  pruneToAddonIds: (allowedAddonIds: string[]) => void;
  // Flat list, mapped into ConfigurationItem.subItems on save (MPL-3694).
  getAllAddons: () => ConfigurationItemAddon[];
};

export type AddonsConfigStore = AddonsConfigState & AddonsConfigActions;

export const createAddonsConfigStore = (
  initialAddons: ConfigurationItemAddon[] = [],
  dietId: string | null = null,
) =>
  createStore<AddonsConfigStore>()((set, get) => ({
    addons: initialAddons,
    dietId,

    seedAddons: (addons) => set({ addons }),

    getAddonEntries: (addonId) =>
      get().addons.filter((entry) => entry.addonId === addonId),

    setAddonEntries: (addonId, entries) =>
      set((state) => ({
        addons: [
          ...state.addons.filter((entry) => entry.addonId !== addonId),
          ...entries.map(({ date, quantity }) => ({
            addonId,
            date,
            quantity,
          })),
        ],
      })),

    pruneToDates: (allowedDates) =>
      set((state) => {
        const allowed = new Set(allowedDates);
        const next = state.addons.filter((entry) => allowed.has(entry.date));
        // Return the same state reference when nothing was removed so no
        // spurious store update (and cart PUT) is triggered.
        return next.length === state.addons.length ? state : { addons: next };
      }),

    pruneToAddonIds: (allowedAddonIds) =>
      set((state) => {
        const allowed = new Set(allowedAddonIds);
        const next = state.addons.filter((entry) => allowed.has(entry.addonId));
        // Same guard as pruneToDates: keep the state reference when nothing was
        // removed, so a valid selection never triggers a spurious cart PUT.
        return next.length === state.addons.length ? state : { addons: next };
      }),

    getAllAddons: () => get().addons,
  }));
